import React, { Component } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import am4lang_pl_PL from "@amcharts/amcharts4/lang/pl_PL";
import { ALARM_SEVERITIES } from "utils/alarmPresentation";

am4core.useTheme(am4themes_animated);

class Chart extends Component {
  state = {
    alarmTooltip: "",
    alarmTooltipId: ""
  };

  requestId = 0;
  chart = null;
  dateAxis = null;
  highlightAxisRanges = [];
  alarmAxisRanges = [];
  alarmSeries = null;
  alarmBullet = null;
  measurementDataReady = false;
  touchCleanup = null;

  componentDidMount() {
    this.renderChart();
  }

  componentDidUpdate(previousProps) {
    if (
      this.configurationKey(previousProps) !==
      this.configurationKey(this.props)
    ) {
      if (this.state.alarmTooltipId)
        this.setState({ alarmTooltip: "", alarmTooltipId: "" });
      this.disposeChart();
      this.renderChart();
    } else {
      if (this.highlightKey(previousProps) !== this.highlightKey(this.props))
        this.addHighlightRanges(this.dateAxis);
      if (
        this.alarmMarkerKey(previousProps) !==
        this.alarmMarkerKey(this.props)
      ) {
        this.addAlarmMarkersWhenReady();
        const markers = Array.isArray(this.props.alarmMarkers)
          ? this.props.alarmMarkers
          : [];
        if (
          this.state.alarmTooltipId &&
          !markers.some(
            marker => String(marker.id) === this.state.alarmTooltipId
          )
        )
          this.setState({ alarmTooltip: "", alarmTooltipId: "" });
      } else if (
        this.alarmFocusKey(previousProps) !== this.alarmFocusKey(this.props)
      )
        this.updateAlarmMarkerFocus();
    }
  }

  componentWillUnmount() {
    this.disposeChart();
  }

  exportPdf = () => {
    if (!this.chart)
      return Promise.reject(new Error("Wykres nie jest jeszcze gotowy."));
    this.chart.exporting.title = this.props.exportTitle || "Wykres pomiarów";
    return this.chart.exporting.export("pdf", {
      pageSize: "A4",
      pageOrientation: "landscape",
      fileName: this.props.exportFileName || "wykres-pomiarow",
      addURL: false
    });
  };

  configurationKey(props) {
    const series = Array.isArray(props.series) ? props.series : [];
    return [
      props.activeDevice,
      props.activeTimeButton,
      props.rangeFrom,
      props.rangeTo,
      props.visualization,
      props.showPoints,
      props.sharedTemperatureAxis,
      props.refreshKey,
      series
        .map(
          item =>
            `${item.id}:${item.functionType}:${item.unit}:${item.label}`
        )
        .join(",")
    ].join("|");
  }

  highlightKey(props) {
    const highlights = Array.isArray(props.highlightRanges)
      ? props.highlightRanges
      : [];
    return highlights
      .map(
        item =>
          `${item.start}-${item.end}-${item.status}-${item.label ||
            ""}-${item.amount || ""}-${item.actionKey || ""}`
      )
      .join("|");
  }

  alarmMarkerKey(props) {
    const markers = Array.isArray(props.alarmMarkers)
      ? props.alarmMarkers
      : [];
    return markers
      .map(
        marker =>
          `${marker.id}-${marker.timestamp}-${marker.color}-${
            marker.tooltip || ""
          }`
      )
      .join("|");
  }

  alarmFocusKey(props) {
    const markers = Array.isArray(props.alarmMarkers)
      ? props.alarmMarkers
      : [];
    const focused = markers.find(marker => marker.focused);
    return focused ? String(focused.id) : "";
  }

  disposeChart() {
    this.requestId++;
    this.highlightAxisRanges = [];
    this.alarmAxisRanges = [];
    this.alarmSeries = null;
    this.alarmBullet = null;
    this.measurementDataReady = false;
    this.dateAxis = null;
    if (this.touchCleanup) {
      this.touchCleanup();
      this.touchCleanup = null;
    }
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  }

  enablePinchZoom(chart, dateAxis) {
    const element = chart.svgContainer.htmlElement;
    let gesture = null;
    const distance = touches => {
      const x = touches[0].clientX - touches[1].clientX;
      const y = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(x * x + y * y);
    };

    const handleTouchStart = event => {
      if (event.touches.length !== 2) return;
      gesture = {
        distance: distance(event.touches),
        start: dateAxis.start,
        end: dateAxis.end,
        cursorBehavior: chart.cursor ? chart.cursor.behavior : null
      };
      if (chart.cursor)
        chart.cursor.behavior = "none";
    };

    const handleTouchMove = event => {
      if (!gesture || event.touches.length !== 2) return;
      const currentDistance = distance(event.touches);
      if (currentDistance <= 0 || gesture.distance <= 0) return;

      const rect = element.getBoundingClientRect();
      const centerX =
        (event.touches[0].clientX + event.touches[1].clientX) / 2;
      const location = Math.max(
        0,
        Math.min(1, (centerX - rect.left) / rect.width)
      );
      const initialRange = gesture.end - gesture.start;
      const range = Math.max(
        0.001,
        Math.min(
          1,
          initialRange *
            Math.pow(gesture.distance / currentDistance, 0.82)
        )
      );
      const anchor = gesture.start + initialRange * location;
      let start = anchor - range * location;
      start = Math.max(0, Math.min(1 - range, start));

      dateAxis.zoom({ start, end: start + range }, false, false);
      event.preventDefault();
    };

    const handleWheel = event => {
      const rect = element.getBoundingClientRect();
      const location = Math.max(
        0,
        Math.min(1, (event.clientX - rect.left) / rect.width)
      );
      const currentRange = dateAxis.end - dateAxis.start;
      const delta =
        event.deltaMode === 1
          ? event.deltaY * 16
          : event.deltaMode === 2
            ? event.deltaY * 100
            : event.deltaY;
      const range = Math.max(
        0.001,
        Math.min(1, currentRange * Math.exp(delta * 0.02))
      );
      const anchor = dateAxis.start + currentRange * location;
      let start = anchor - range * location;
      start = Math.max(0, Math.min(1 - range, start));

      dateAxis.zoom({ start, end: start + range }, false, false);
      event.preventDefault();
    };

    const handleTouchEnd = event => {
      if (event.touches.length >= 2) return;
      if (gesture && chart.cursor && gesture.cursorBehavior)
        chart.cursor.behavior = gesture.cursorBehavior;
      gesture = null;
    };

    const previousTouchAction = element.style.touchAction;
    element.style.touchAction = "pan-y";
    element.addEventListener("touchstart", handleTouchStart, {
      passive: true
    });
    element.addEventListener("touchmove", handleTouchMove, {
      passive: false
    });
    element.addEventListener("touchend", handleTouchEnd, {
      passive: true
    });
    element.addEventListener("touchcancel", handleTouchEnd, {
      passive: true
    });
    element.addEventListener("wheel", handleWheel, {
      passive: false
    });

    this.touchCleanup = () => {
      element.removeEventListener("touchstart", handleTouchStart);
      element.removeEventListener("touchmove", handleTouchMove);
      element.removeEventListener("touchend", handleTouchEnd);
      element.removeEventListener("touchcancel", handleTouchEnd);
      element.removeEventListener("wheel", handleWheel);
      element.style.touchAction = previousTouchAction;
    };
  }

  loadSeries(seriesDefinition, attempt = 0) {
    const parameters = new URLSearchParams({
      ile: String(this.props.activeTimeButton),
      id: String(seriesDefinition.id),
      device: String(this.props.activeDevice)
    });
    if (
      Number.isFinite(this.props.rangeFrom) &&
      Number.isFinite(this.props.rangeTo)
    ) {
      parameters.set("from", String(this.props.rangeFrom));
      parameters.set("to", String(this.props.rangeTo));
    }
    return fetch(`https://hgsystem.pl/getjsondata.php?${parameters}`, {
      cache: "no-store"
    })
      .then(response => {
        if (!response.ok)
          throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then(response => {
        if (!Array.isArray(response)) return [];
        return response
          .map(element => {
            const timestamp = Number(element[0]);
            const value = Number(element[1]);
            if (!isFinite(timestamp) || !isFinite(value)) return null;
            return {
              date: new Date(timestamp),
              value
            };
          })
          .filter(Boolean);
      })
      .catch(error => {
        if (attempt >= 2) throw error;
        return new Promise(resolve =>
          window.setTimeout(resolve, 400 * (attempt + 1))
        ).then(() => this.loadSeries(seriesDefinition, attempt + 1));
      });
  }

  addValueAxis(chart, definition, index) {
    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    const color = am4core.color(definition.color);
    valueAxis.title.text = definition.axisTitle;
    valueAxis.title.fill = color;
    valueAxis.renderer.labels.template.fill = color;
    valueAxis.renderer.line.stroke = color;
    valueAxis.renderer.line.strokeOpacity = 0.5;
    valueAxis.renderer.minWidth = 45;
    valueAxis.tooltip.disabled = true;
    valueAxis.renderer.opposite = index % 2 === 1;
    valueAxis.renderer.offset = Math.floor(index / 2) * 55;
    if (index > 0)
      valueAxis.renderer.grid.template.disabled = true;
    return valueAxis;
  }

  addSeries(chart, definition, data, valueAxis) {
    const isColumn = this.props.visualization === "column";
    const series = chart.series.push(
      isColumn
        ? new am4charts.ColumnSeries()
        : new am4charts.LineSeries()
    );
    const color = am4core.color(definition.color);
    series.name = definition.label;
    series.data = data;
    series.dataFields.dateX = "date";
    series.dataFields.valueY = "value";
    series.yAxis = valueAxis;
    series.stroke = color;
    series.fill = color;
    series.showOnInit = false;
    series.interpolationDuration = 0;
    series.defaultState.transitionDuration = 0;
    series.hiddenState.transitionDuration = 0;
    series.tooltipText =
      `[bold]{name}[/]\n{dateX.formatDate("dd MMM yyyy, HH:mm")}: ` +
      `{valueY} ${definition.unit}`;

    if (isColumn) {
      series.columns.template.width = am4core.percent(75);
      series.columns.template.fillOpacity = 0.75;
      series.columns.template.strokeOpacity = 0.9;
    } else {
      series.strokeWidth = 2;
      series.fillOpacity = 0.08;
      series.connect = false;
      series.minBulletDistance = 18;
      if (this.props.showPoints) {
        const bullet = series.bullets.push(new am4charts.CircleBullet());
        bullet.circle.radius = 4;
        bullet.circle.fill = color;
        bullet.circle.stroke = am4core.color("#ffffff");
        bullet.circle.strokeWidth = 1.5;
      }
    }
    return series;
  }

  addHighlightRanges(dateAxis) {
    if (!dateAxis) return;
    this.highlightAxisRanges.forEach(range =>
      dateAxis.axisRanges.removeValue(range)
    );
    this.highlightAxisRanges = [];
    const highlights = Array.isArray(this.props.highlightRanges)
      ? this.props.highlightRanges
      : [];
    highlights.forEach(highlight => {
      const start = Number(highlight.start);
      const end = Number(highlight.end);
      if (!Number.isFinite(start) || !Number.isFinite(end) || end < start)
        return;
      const range = dateAxis.axisRanges.create();
      range.date = new Date(start * 1000);
      range.endDate = new Date(Math.max(end, start + 60) * 1000);
      range.axisFill.fill = am4core.color(
        highlight.status === "saved" ? "#87CB16" : "#FFA534"
      );
      range.axisFill.fillOpacity =
        highlight.status === "saved" ? 0.14 : 0.2;
      range.grid.strokeOpacity = 0;
      range.label.text =
        highlight.status === "candidate"
          ? `+ ${Number(highlight.amount).toFixed(1)}`
          : highlight.label || "Odbiór";
      range.label.inside = highlight.status !== "candidate";
      range.label.location = 0.5;
      range.label.rotation = 0;
      range.label.horizontalCenter = "middle";
      if (highlight.status === "candidate") {
        range.label.fill = am4core.color("#ffffff");
        range.label.fontSize = 16;
        range.label.fontWeight = "bold";
        range.label.padding(1, 7, 2, 7);
        range.label.background.fill = am4core.color("#87CB16");
        range.label.background.fillOpacity = 1;
        range.label.background.stroke = am4core.color("#ffffff");
        range.label.background.strokeWidth = 2;
        range.label.cursorOverStyle = am4core.MouseCursorStyle.pointer;
        range.label.events.on("hit", () => {
          if (this.props.onHighlightAction)
            this.props.onHighlightAction(highlight);
        });
      } else {
        range.label.fill = am4core.color("#555555");
      }
      this.highlightAxisRanges.push(range);
    });
  }

  // Creates a hidden 0..1 axis used only to keep alarm points at the top of
  // the plot, independently from temperature and volume scales.
  createAlarmMarkerSeries(chart, dateAxis) {
    const markerAxis = chart.yAxes.push(new am4charts.ValueAxis());
    markerAxis.min = 0;
    markerAxis.max = 1;
    markerAxis.strictMinMax = true;
    markerAxis.renderer.disabled = true;
    markerAxis.cursorTooltipEnabled = false;

    const series = chart.series.push(new am4charts.LineSeries());
    series.dataFields.dateX = "date";
    series.dataFields.valueY = "level";
    series.xAxis = dateAxis;
    series.yAxis = markerAxis;
    series.strokeOpacity = 0;
    series.fillOpacity = 0;
    series.hiddenInLegend = true;
    series.maskBullets = false;
    series.showOnInit = false;

    const bullet = series.bullets.push(new am4charts.CircleBullet());
    bullet.circle.radius = 8;
    bullet.circle.propertyFields.fill = "color";
    bullet.circle.stroke = am4core.color("#ffffff");
    bullet.circle.strokeWidth = 2;
    bullet.circle.interactionsEnabled = true;
    bullet.circle.cursorOverStyle = am4core.MouseCursorStyle.pointer;
    bullet.dy = 8;
    bullet.showOnInit = false;
    series.interpolationDuration = 0;
    series.defaultState.transitionDuration = 0;
    series.hiddenState.transitionDuration = 0;
    bullet.defaultState.transitionDuration = 0;
    bullet.hiddenState.transitionDuration = 0;
    bullet.circle.adapter.add("radius", (radius, target) => {
      const context =
        target.dataItem && target.dataItem.dataContext;
      return context && context.focused ? 11 : 8;
    });
    const markerContext = target => {
      let current = target;
      while (current) {
        if (current.dataItem && current.dataItem.dataContext)
          return current.dataItem.dataContext;
        current = current.parent;
      }
      return null;
    };
    const showMarkerDetails = event => {
      const context = markerContext(event.target);
      if (context) this.showAlarmDetails(context);
    };
    bullet.circle.events.on("over", showMarkerDetails);
    bullet.circle.events.on("hit", showMarkerDetails);
    this.alarmBullet = bullet;
    this.alarmSeries = series;
  }

  showAlarmDetails = marker => {
    if (!marker) return;
    const id = String(marker.id || marker.timestamp || "");
    if (marker.id && !marker.focused && this.props.onAlarmSelect)
      this.props.onAlarmSelect(String(marker.id));
    const tooltip = marker.alarmDetails
      ? {
          ...marker.alarmDetails,
          color: marker.alarmColor || marker.color,
          severity: marker.severity
        }
      : {
        severityLabel: "Alarm",
        status: "Aktywny",
        message: String(marker.tooltip || "Alarm")
          .replace(/\[bold\]/g, "")
          .replace(/\[\/\]/g, "")
      };
    if (this.state.alarmTooltipId === id) return;
    this.setState({
      alarmTooltip: tooltip,
      alarmTooltipId: id
    });
  };

  // Uses cursor time rather than amCharts bullet propagation, which is
  // inconsistent in the legacy amCharts version used by this panel.
  enableAlarmCursorHitTesting(chart, dateAxis) {
    chart.cursor.events.on("cursorpositionchanged", () => {
      const markers = Array.isArray(this.props.alarmMarkers)
        ? this.props.alarmMarkers
        : [];
      if (markers.length === 0 || !chart.plotContainer.pixelWidth) return;
      const axisPosition =
        typeof dateAxis.toAxisPosition === "function"
          ? dateAxis.toAxisPosition(chart.cursor.xPosition)
          : chart.cursor.xPosition;
      const cursorDate = dateAxis.positionToDate(axisPosition);
      if (!cursorDate || !Number.isFinite(cursorDate.getTime())) return;
      const visibleMinimum = Number(dateAxis.minZoomed || dateAxis.min);
      const visibleMaximum = Number(dateAxis.maxZoomed || dateAxis.max);
      if (
        !Number.isFinite(visibleMinimum) ||
        !Number.isFinite(visibleMaximum) ||
        visibleMaximum <= visibleMinimum
      )
        return;
      const toleranceSeconds =
        ((visibleMaximum - visibleMinimum) / 1000) *
        (18 / chart.plotContainer.pixelWidth);
      const cursorSeconds = cursorDate.getTime() / 1000;
      let nearest = null;
      let nearestDistance = Infinity;
      markers.forEach(marker => {
        const timestamp = Number(marker.timestamp);
        if (!Number.isFinite(timestamp)) return;
        const distance = Math.abs(timestamp - cursorSeconds);
        if (distance < nearestDistance) {
          nearest = marker;
          nearestDistance = distance;
        }
      });
      if (nearest && nearestDistance <= toleranceSeconds)
        this.showAlarmDetails(nearest);
    });
  }

  // Adds severity-colored vertical lines and updates the larger alarm points.
  // Both layers update independently from measurements to preserve chart zoom.
  addAlarmMarkersWhenReady() {
    if (!this.measurementDataReady) return;
    this.addAlarmMarkers(this.dateAxis);
  }

  updateAlarmMarkerFocus() {
    const markers = Array.isArray(this.props.alarmMarkers)
      ? this.props.alarmMarkers
      : [];
    markers.forEach((marker, index) => {
      const range = this.alarmAxisRanges[index];
      if (range) {
        range.grid.strokeOpacity = marker.focused ? 1 : 0.85;
        range.grid.strokeWidth = marker.focused ? 4 : 2;
      }
      if (this.alarmSeries && this.alarmSeries.data[index])
        this.alarmSeries.data[index].focused = Boolean(marker.focused);
    });
    if (this.alarmSeries && this.alarmBullet)
      this.alarmSeries.dataItems.each(dataItem => {
        const marker = markers.find(
          item =>
            dataItem.dataContext &&
            String(item.id) === String(dataItem.dataContext.id)
        );
        if (dataItem.dataContext)
          dataItem.dataContext.focused = Boolean(marker && marker.focused);
        const itemBullet = dataItem.bullets.getKey(this.alarmBullet.uid);
        if (itemBullet && itemBullet.circle)
          itemBullet.circle.radius = marker && marker.focused ? 11 : 8;
      });
  }

  addAlarmMarkers(dateAxis) {
    if (!dateAxis) return;
    this.alarmAxisRanges.forEach(range =>
      dateAxis.axisRanges.removeValue(range)
    );
    this.alarmAxisRanges = [];
    const markers = Array.isArray(this.props.alarmMarkers)
      ? this.props.alarmMarkers
      : [];
    markers.forEach(marker => {
      const timestamp = Number(marker.timestamp);
      if (!Number.isFinite(timestamp)) return;
      const color = am4core.color(marker.color || "#777777");
      const range = dateAxis.axisRanges.create();
      range.date = new Date(timestamp * 1000);
      range.grid.stroke = color;
      range.grid.adapter.add("stroke", () => color);
      range.grid.disabled = false;
      range.grid.location = 0;
      range.grid.strokeOpacity = marker.focused ? 1 : 0.85;
      range.grid.strokeWidth = marker.focused ? 4 : 2;
      range.grid.above = true;
      range.grid.interactionsEnabled = true;
      range.grid.cursorOverStyle = am4core.MouseCursorStyle.pointer;
      range.grid.events.on("over", () => this.showAlarmDetails(marker));
      range.grid.events.on("hit", () => this.showAlarmDetails(marker));
      range.label.disabled = true;
      this.alarmAxisRanges.push(range);
    });
    if (this.alarmSeries) {
      this.alarmSeries.data = markers
        .map(marker => {
          const timestamp = Number(marker.timestamp);
          if (!Number.isFinite(timestamp)) return null;
          return {
            date: new Date(timestamp * 1000),
            level: 1,
            color: am4core.color(marker.color || "#777777"),
            tooltip: marker.tooltip || "Alarm",
            id: marker.id,
            timestamp: marker.timestamp,
            alarmDetails: marker.alarmDetails,
            alarmColor: marker.color,
            severity: marker.severity,
            focused: Boolean(marker.focused)
          };
        })
        .filter(Boolean);
    }
  }

  renderChart() {
    const definitions = Array.isArray(this.props.series)
      ? this.props.series
      : [];
    if (!this.props.activeDevice || definitions.length === 0) return;

    if (this.props.onLoadingChange) this.props.onLoadingChange(true);
    const chart = am4core.create("chartdiv", am4charts.XYChart);
    this.chart = chart;
    const currentRequestId = ++this.requestId;

    chart.language.locale = am4lang_pl_PL;
    chart.dateFormatter.utc = false;
    const temperatureDefinitions = definitions.filter(
      definition => definition.functionType === 1
    );
    const shareTemperatureAxis =
      this.props.sharedTemperatureAxis &&
      temperatureDefinitions.length > 1;
    const axisCount = shareTemperatureAxis
      ? definitions.length - temperatureDefinitions.length + 1
      : definitions.length;
    const axesPerSide = Math.ceil(axisCount / 2);
    chart.paddingLeft = axesPerSide > 1 ? 55 : 0;
    chart.paddingRight = axesPerSide > 1 ? 75 : 20;

    const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
    this.dateAxis = dateAxis;
    dateAxis.renderer.grid.template.location = 0;
    dateAxis.tooltipDateFormat = "dd MMM yyyy, HH:mm";
    dateAxis.groupData = true;
    dateAxis.groupCount = 600;
    dateAxis.minZoomCount = 2;
    this.addHighlightRanges(dateAxis);
    this.createAlarmMarkerSeries(chart, dateAxis);

    this.enablePinchZoom(chart, dateAxis);

    chart.legend = new am4charts.Legend();
    chart.legend.position = "top";

    chart.cursor = new am4charts.XYCursor();
    chart.cursor.behavior = "panX";
    chart.cursor.xAxis = dateAxis;
    chart.cursor.maxPanOut = 0;
    chart.cursor.maxTooltipDistance = 30;
    this.enableAlarmCursorHitTesting(chart, dateAxis);

    Promise.all(
      definitions.map(definition =>
        this.loadSeries(definition)
          .then(data => ({ data, error: null }))
          .catch(error => ({ data: [], error }))
      )
    )
      .then(results => {
        if (
          currentRequestId !== this.requestId ||
          !this.chart ||
          this.chart !== chart
        )
          return;
        const successfulResults = results.filter(result => !result.error);
        if (successfulResults.length === 0)
          throw results[0].error || new Error("Measurement data unavailable");
        const dataSets = results.map(result => result.data);

        if (this.props.onDataLoaded) {
          const dataByInput = {};
          definitions.forEach((definition, index) => {
            dataByInput[String(definition.id)] = dataSets[index];
          });
          this.props.onDataLoaded(dataByInput);
        }

        // Temperature inputs can share one scale while keeping separate colors.
        let sharedTemperatureValueAxis = null;
        let valueAxisIndex = 0;
        const createdSeries = definitions.map((definition, index) => {
          let valueAxis;
          if (shareTemperatureAxis && definition.functionType === 1) {
            if (!sharedTemperatureValueAxis) {
              sharedTemperatureValueAxis = this.addValueAxis(
                chart,
                {
                  ...definition,
                  axisTitle: "Temperatura [°C]",
                  color: "#66757f"
                },
                valueAxisIndex++
              );
            }
            valueAxis = sharedTemperatureValueAxis;
          } else {
            valueAxis = this.addValueAxis(
              chart,
              definition,
              valueAxisIndex++
            );
          }
          return this.addSeries(
            chart,
            definition,
            dataSets[index],
            valueAxis
          );
        });

        if (createdSeries.length > 0) {
          const scrollbar = new am4charts.XYChartScrollbar();
          createdSeries.forEach(series => scrollbar.series.push(series));
          chart.scrollbarX = scrollbar;
        }
        if (successfulResults.length < results.length) {
          const warning = chart.plotContainer.createChild(am4core.Label);
          warning.text =
            "Część serii jest chwilowo niedostępna. Pozostałe dane wyświetlono.";
          warning.fill = am4core.color("#A56A00");
          warning.fontSize = 12;
          warning.align = "right";
          warning.valign = "top";
          warning.marginTop = 6;
          warning.marginRight = 8;
        }
        chart.events.once("datavalidated", () => {
          if (currentRequestId !== this.requestId || this.chart !== chart)
            return;
          this.measurementDataReady = true;
          this.addAlarmMarkers(dateAxis);
        });
        chart.invalidateData();
        if (this.props.onLoadingChange) this.props.onLoadingChange(false);
      })
      .catch(() => {
        if (currentRequestId !== this.requestId || this.chart !== chart)
          return;
        if (this.props.onLoadingChange) this.props.onLoadingChange(false);
        const label = chart.plotContainer.createChild(am4core.Label);
        label.text = "Nie udało się pobrać danych wykresu.";
        label.horizontalCenter = "middle";
        label.verticalCenter = "middle";
        label.align = "center";
        label.valign = "middle";
      });
  }

  render() {
    const alarmTooltip = this.state.alarmTooltip || null;
    const alarmColor =
      (alarmTooltip &&
        ALARM_SEVERITIES[String(alarmTooltip.severity)] &&
        ALARM_SEVERITIES[String(alarmTooltip.severity)].color) ||
      (alarmTooltip && alarmTooltip.color) ||
      "#FB404B";
    const alarmMetrics = alarmTooltip
      ? [
          { label: "Temperatura", value: alarmTooltip.temperature },
          { label: "Objętość", value: alarmTooltip.volume },
          {
            label: "Stan zbiornika",
            value: alarmTooltip.tankState,
            color: alarmTooltip.tankStateColor
          }
        ].filter(item => item.value)
      : [];
    return (
      <div>
        <div id="chartdiv" style={{ width: "100%", height: "460px" }} />
        {Array.isArray(this.props.alarmMarkers) &&
          this.props.alarmMarkers.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: "6px 14px",
                paddingTop: 6
              }}
            >
              {Object.keys(ALARM_SEVERITIES).map(key => {
                const severity = ALARM_SEVERITIES[key];
                return (
                  <span key={key} style={{ whiteSpace: "nowrap" }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        marginRight: 5,
                        backgroundColor: severity.color
                      }}
                    />
                    {severity.label}
                  </span>
                );
              })}
            </div>
          )}
        {alarmTooltip && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "8px 10px 0",
              boxSizing: "border-box"
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "100%",
                maxHeight: 260,
                overflowY: "auto",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                textAlign: "left",
                padding: "14px 16px 12px 20px",
                position: "relative",
                boxSizing: "border-box",
                border: "1px solid #dce4e8",
                borderLeft: `5px solid ${alarmColor}`,
                borderRadius: 8,
                background: `linear-gradient(90deg, ${alarmColor}10 0%, #ffffff 36%)`,
                color: "#333333",
                boxShadow: "0 3px 12px rgba(0,0,0,0.12)"
              }}
            >
              <button
                type="button"
                aria-label="Zamknij opis alarmu"
                onClick={() =>
                  this.setState({ alarmTooltip: "", alarmTooltipId: "" })
                }
                style={{
                  position: "absolute",
                  top: 8,
                  right: 10,
                  border: 0,
                  background: "transparent",
                  color: "#66757f",
                  fontSize: 22,
                  lineHeight: 1,
                  cursor: "pointer"
                }}
              >
                ×
              </button>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 8,
                  paddingRight: 28,
                  marginBottom: 4
                }}
              >
                <strong
                  style={{
                    color: alarmColor,
                    fontSize: 16,
                    letterSpacing: "0.02em"
                  }}
                >
                  {alarmTooltip.severityLabel || "Alarm"}
                </strong>
                <span
                  style={{
                    padding: "2px 8px",
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: "bold",
                    color:
                      alarmTooltip.status === "Aktywny"
                        ? "#a93226"
                        : "#287c45",
                    backgroundColor:
                      alarmTooltip.status === "Aktywny"
                        ? "#fdeceb"
                        : "#e7f6ec"
                  }}
                >
                  {(alarmTooltip.status || "Aktywny").toUpperCase()}
                </span>
              </div>
              {alarmTooltip.date && (
                <div
                  style={{
                    color: "#687780",
                    fontSize: 12,
                    marginBottom: 9
                  }}
                >
                  <span role="img" aria-label="Data i czas">
                    🕒
                  </span>{" "}
                  {alarmTooltip.date}
                </div>
              )}
              <div
                style={{
                  fontSize: 14,
                  lineHeight: 1.45,
                  marginBottom: alarmMetrics.length > 0 ? 10 : 4
                }}
              >
                {alarmTooltip.message}
              </div>
              {alarmMetrics.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    marginBottom: 9
                  }}
                >
                  {alarmMetrics.map(metric => (
                    <div
                      key={metric.label}
                      style={{
                        flex: "1 1 130px",
                        minWidth: 0,
                        padding: "7px 9px",
                        borderRadius: 5,
                        background: metric.color
                          ? `linear-gradient(135deg, ${metric.color}20, #ffffff)`
                          : "#f5f8fa",
                        border: `1px solid ${
                          metric.color ? `${metric.color}70` : "#e4eaee"
                        }`
                      }}
                    >
                      <div style={{ color: "#71808a", fontSize: 11 }}>
                        {metric.label}
                      </div>
                      <strong style={metric.color ? { color: metric.color } : {}}>
                        {metric.value}
                      </strong>
                    </div>
                  ))}
                </div>
              )}
              {(alarmTooltip.value ||
                alarmTooltip.threshold ||
                (alarmTooltip.code !== null &&
                  alarmTooltip.code !== undefined) ||
                alarmTooltip.occurrenceId) && (
                <div
                  style={{
                    paddingTop: 7,
                    borderTop: "1px solid #e6ecef",
                    color: "#71808a",
                    fontSize: 11
                  }}
                >
                  {alarmTooltip.value && `Wartość: ${alarmTooltip.value}`}
                  {alarmTooltip.threshold &&
                    `${alarmTooltip.value ? " · " : ""}Próg: ${
                      alarmTooltip.threshold
                    }`}
                  {alarmTooltip.code !== null &&
                    alarmTooltip.code !== undefined &&
                    `${alarmTooltip.value || alarmTooltip.threshold ? " · " : ""}Kod: ${
                      alarmTooltip.code
                    }`}
                  {alarmTooltip.occurrenceId &&
                    ` · ID wystąpienia: ${alarmTooltip.occurrenceId}`}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default Chart;



// WEBPACK FOOTER //
// ./src/components/Charts/chart.jsx